import { useTranslation } from 'react-i18next'

import {
  SHIPMENT_STAGES,
  ShipmentActiveFilters,
  ShipmentFiltersButton,
  ShipmentFiltersPanel,
  ShipmentListToolbar,
  isSpecificOwner,
} from '#/entities/shipment'
import { ShipmentListCard } from '#/widgets/shipment-list-card'
import { useShipmentSidebar } from '#/widgets/shipment-sidebar'
import { useMassCallAction } from '#/features/mass-call'
import { MassEmailDialog, useMassEmailAction } from '#/features/mass-email'
import { MassSmsDialog, useMassSmsAction } from '#/features/mass-sms'
import { DataTable } from '#/shared/ui/data-table'
import { USER_DROPDOWN_VARIANTS } from '#/shared/ui/user-dropdown'
import { useLeadsDocumentTitle } from '../model/use-leads-document-title'
import { useLeadsPage } from '../model/use-leads-page'
import { useLeadsColumns } from './leads-columns'

export function LeadsPage() {
  const { t } = useTranslation()
  useLeadsDocumentTitle()
  const openShipmentSidebar = useShipmentSidebar(SHIPMENT_STAGES.LEAD)
  const {
    rows,
    totalItems,
    totalTariff,
    totalDeposit,
    isLoading,
    isFetching,
    isFiltered,
    selectedCount,
    selectedIds,
    resetSelection,
    rangeFrom,
    rangeTo,
    rangeDisabled,
    pagination,
    onSelectionChange,
    onRangeChange,
    filters,
    status,
  } = useLeadsPage()
  const columns = useLeadsColumns({
    showOwner: !isSpecificOwner(filters.userId),
  })
  const massSms = useMassSmsAction(rows, selectedIds)
  const massEmail = useMassEmailAction(rows, selectedIds)
  const massCall = useMassCallAction(rows, selectedIds, {
    onStarted: resetSelection,
  })

  return (
    <div className="flex flex-col gap-2.5 md:min-h-0 md:flex-1">
      <ShipmentListToolbar
        stage={SHIPMENT_STAGES.LEAD}
        status={status}
        selectedCount={selectedCount}
        onMassSms={massSms.openSms}
        onMassEmail={massEmail.openEmail}
        onAutoDial={massCall.startAutoDial}
        autoDialDisabledReason={massCall.disabledReason}
        searchPlaceholder={t('toolbar.searchLeads')}
        newButtonLabel={t('toolbar.newLead')}
        filters={({ disabled }) => (
          <ShipmentFiltersButton state={filters} disabled={disabled} />
        )}
        filtersPanel={
          <ShipmentFiltersPanel
            state={filters}
            isFetching={isFetching}
            forceClose={selectedCount > 0}
          />
        }
        rangeFrom={rangeFrom}
        rangeTo={rangeTo}
        total={totalItems}
        totalTariff={totalTariff}
        totalDeposit={totalDeposit}
        onRangeChange={onRangeChange}
        rangeDisabled={rangeDisabled}
        isFiltered={isFiltered}
        ownerUserId={filters.userId}
        onOwnerUserChange={filters.setUserId}
        userDropdownVariant={USER_DROPDOWN_VARIANTS.TEAMS}
        activeFilters={
          filters.count > 0 ? (
            <ShipmentActiveFilters state={filters} />
          ) : undefined
        }
      />
      <DataTable
        storageKey="leads-table-pins"
        columns={columns}
        data={rows}
        selectable
        onRowClick={(row) => openShipmentSidebar(row.shipmentId, row.status)}
        onSelectionChange={onSelectionChange}
        selectedIds={selectedIds}
        emptyVariant={isFiltered ? 'filtered' : 'initial'}
        pagination={pagination}
        isLoading={isLoading}
        minWidth={900}
        fillHeight
        renderCard={(row) => (
          <ShipmentListCard row={row} stage={SHIPMENT_STAGES.LEAD} />
        )}
      />
      <MassSmsDialog
        open={massSms.open}
        onOpenChange={massSms.onOpenChange}
        onSent={resetSelection}
        viewModel={massSms.viewModel}
      />
      <MassEmailDialog
        open={massEmail.open}
        onOpenChange={massEmail.onOpenChange}
        onSent={resetSelection}
        viewModel={massEmail.viewModel}
      />
    </div>
  )
}
